import type { QuestionInterrupt } from "#features/conversations/lib/buildPendingQuestion.ts";
import { updateMessages } from "#features/conversations/lib/toolApprovalRecords.ts";
import type { ConversationModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { QuestionRecord } from "#types/InteractiveQuestion.ts";
import type { QuestionAnswer } from "#types/types.ts";
import type { QueryClient } from "@tanstack/react-query";

export type QuestionOutcome = { status: "answered"; answers: QuestionAnswer[] } | { status: "dismissed" };

// The conversation's open question and the message that asked it.
export function findPendingQuestion(conversation: ConversationModel | undefined): { messageId: string; question: QuestionRecord } | null {
    const message = conversation?.messages.findLast(({ question }) => question?.status === "pending");

    return message?.question ? { messageId: message.id, question: message.question } : null;
}

/** Shows the question a run ended on, on the message that asked it. */
export function showPendingQuestion(queryClient: QueryClient, conversationId: string, messageId: string, question: QuestionInterrupt, promptFeature?: string) {
    updateMessages(queryClient, conversationId, message => (message.id === messageId
        ? { ...message, question: { ...question, status: "pending", answers: null, ...(promptFeature && { promptFeature }) } }
        : message));
}

/** Closes the open question: the card gives way to the composer, and an answer shows on the message. The next read replays the same state. */
export function settlePendingQuestion(queryClient: QueryClient, conversationId: string, interruptId: string, outcome: QuestionOutcome) {
    // A read already in flight predates the answer, so it must not bring the card back.
    void queryClient.cancelQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) });
    updateMessages(queryClient, conversationId, message => (message.question?.interruptId === interruptId && message.question.status === "pending"
        ? { ...message, question: { ...message.question, ...outcome } }
        : message));
}

/**
 * Shows the open question as dismissed when a message is sent past it; core supersedes the open pause on its own.
 * Returns whether the card changed.
 */
export function cancelPendingQuestion(queryClient: QueryClient, conversationId: string): boolean {
    const pending = findPendingQuestion(queryClient.getQueryData<ConversationModel>(AiQueryKeys.GetConversation(conversationId)));
    if (!pending) {
        return false;
    }
    settlePendingQuestion(queryClient, conversationId, pending.question.interruptId, { status: "dismissed" });

    return true;
}
